import { presentLexError } from '@/lib/user-notice';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getQuickAccessLaws, addQuickAccessLaw } from '@/lib/quickAccess';
import { getLaws } from '@/lib/laws';
import type { QuickAccessLaw } from '@/types';
import type { Law } from '@/types';

export function HomePage() {
  const { user } = useAuth();
  const [quickLaws, setQuickLaws] = useState<QuickAccessLaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [allLaws, setAllLaws] = useState<Law[]>([]);
  const [addingLawId, setAddingLawId] = useState<string | null>(null);

  const firstName = user?.name ? user.name.split(' ')[0] : 'usuario';

  const load = async () => {
    setError('');
    try {
      const list = await getQuickAccessLaws();
      setQuickLaws(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar accesos rápidos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAddModal = async () => {
    setShowAddModal(true);
    setError('');
    try {
      const laws = await getLaws();
      setAllLaws(laws);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar leyes.');
    }
  };

  const handleAddLaw = async (law: Law) => {
    setAddingLawId(law.id);
    try {
      await addQuickAccessLaw(law.id, law.title);
      await load();
      setShowAddModal(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al agregar.');
    } finally {
      setAddingLawId(null);
    }
  };

  const quickLawIds = new Set(quickLaws.map((l) => l.lawId));
  const lawsToShow = allLaws.filter((l) => !quickLawIds.has(l.id));

  return (
    <main className="lex-page">
      <div className="lex-stack">

        {/* Hero card */}
        <section
          style={{
            background: 'var(--color-navy)',
            borderRadius: 14,
            padding: '24px 20px',
            color: 'var(--color-bg)',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              opacity: 0.55,
              marginBottom: 8,
            }}
          >
            Panel personal
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.04em',
            }}
          >
            Hola, {firstName}.
          </h2>
        </section>

        {/* Novedades section */}
        <section>
          <div className="lex-section-kicker" style={{ marginBottom: 10 }}>
            Novedades
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
            <Link
              to="/feed"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: '16px 14px',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 12,
                textDecoration: 'none',
                minHeight: 90,
              }}
            >
              <div style={{ fontSize: 22, marginBottom: 6, color: 'var(--color-accent)' }}>
                <i className="ri-file-list-3-line" aria-hidden />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)', letterSpacing: '-0.02em' }}>
                Noticias
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                Feed jurídico
              </div>
            </Link>
          </div>
        </section>

        {/* Acceso rápido section */}
        <section>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <div className="lex-section-kicker">Acceso rápido</div>
            <button
              type="button"
              className="lex-button-secondary"
              onClick={openAddModal}
              style={{ fontSize: 13, minHeight: 34, padding: '6px 12px' }}
            >
              <i className="ri-add-line" aria-hidden />
              Agregar
            </button>
          </div>

          {error ? (
            <div style={{ padding: '12px 14px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12 }}>
              <p style={{ margin: 0, color: 'var(--lex-danger)', fontSize: 14 }}>{presentLexError(error)}</p>
            </div>
          ) : null}

          {loading ? (
            <div className="lex-empty" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12 }}>
              <div className="lex-empty-icon">
                <i className="ri-loader-4-line" aria-hidden />
              </div>
              <p style={{ margin: 0 }}>Cargando…</p>
            </div>
          ) : quickLaws.length === 0 ? (
            <div className="lex-empty" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12 }}>
              <div className="lex-empty-icon">
                <i className="ri-bookmark-3-line" aria-hidden />
              </div>
              <p style={{ margin: '0 0 10px', color: 'var(--color-text)', fontWeight: 700 }}>
                Sin leyes fijadas aún.
              </p>
              <p style={{ margin: '0 0 14px', color: 'var(--color-text-muted)', fontSize: 13 }}>
                Agrega leyes de acceso rápido para entrar directo.
              </p>
              <button type="button" className="lex-button-primary" onClick={openAddModal}>
                <i className="ri-add-circle-line" aria-hidden />
                Agregar primera ley
              </button>
            </div>
          ) : (
            <div
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              {quickLaws.map((law, index) => (
                <Link
                  key={law.lawId}
                  to={`/leyes/${law.lawId}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    textDecoration: 'none',
                    borderBottom: index < quickLaws.length - 1 ? '1px solid var(--color-border-soft)' : 'none',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: 'var(--lex-primary-soft)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-accent)',
                      flexShrink: 0,
                    }}
                  >
                    <i className="ri-book-2-line" style={{ fontSize: 16 }} aria-hidden />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        color: 'var(--color-text)',
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: 1.35,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {law.lawTitle}
                    </div>
                  </div>
                  <i
                    className="ri-arrow-right-s-line"
                    style={{ color: 'var(--color-text-faint)', fontSize: 18 }}
                    aria-hidden
                  />
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Add law modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(10, 22, 40, 0.54)',
            zIndex: 40,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-end',
          }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            style={{
              background: 'var(--color-bg)',
              borderTop: '1px solid var(--color-border)',
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              maxHeight: '82vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '16px 18px',
                borderBottom: '1px solid var(--color-border-soft)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div className="lex-section-kicker" style={{ marginBottom: 4 }}>
                  Biblioteca legal
                </div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--color-text)' }}>
                  Agregar ley
                </h3>
              </div>
              <button
                type="button"
                className="lex-icon-button"
                onClick={() => setShowAddModal(false)}
                aria-label="Cerrar"
              >
                <i className="ri-close-line" aria-hidden />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {lawsToShow.length === 0 ? (
                <div className="lex-empty">
                  <div className="lex-empty-icon">
                    <i className="ri-check-double-line" aria-hidden />
                  </div>
                  <p style={{ margin: 0 }}>
                    {allLaws.length === 0 ? 'Cargando leyes…' : 'Todas las leyes ya están agregadas.'}
                  </p>
                </div>
              ) : (
                <div>
                  {lawsToShow.map((law, index) => (
                    <button
                      key={law.id}
                      type="button"
                      onClick={() => handleAddLaw(law)}
                      disabled={addingLawId !== null}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '14px 18px',
                        textAlign: 'left',
                        cursor: addingLawId !== null ? 'wait' : 'pointer',
                        background: 'none',
                        border: 'none',
                        borderBottom: index < lawsToShow.length - 1 ? '1px solid var(--color-border-soft)' : 'none',
                        color: 'var(--color-text)',
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          background: 'var(--lex-primary-soft)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--color-accent)',
                          flexShrink: 0,
                          fontSize: 16,
                        }}
                      >
                        <i className="ri-book-2-line" aria-hidden />
                      </div>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{law.title}</span>
                      {addingLawId === law.id ? (
                        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Agregando…</span>
                      ) : (
                        <i
                          className="ri-add-circle-line"
                          style={{ fontSize: 20, color: 'var(--color-accent)', flexShrink: 0 }}
                          aria-hidden
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
